import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../auth/msalConfig";
import { BlueprintBackdrop } from "../components/BlueprintBackdrop";
import { PasswordRules, passwordLooksAcceptable } from "../components/PasswordRules";
import { startLocalSession } from "../api/client";
import { theme } from "../theme";
import { SIGN_IN as C, signInSecondaryButton, signInSheet } from "../lib/signInPalette";

/** POST /invites/redeem 409 account_conflict and the Google callback's ?google_error=account_conflict (lib/inviteRedemption.ts). */
const ACCOUNT_CONFLICT_MESSAGE = "That email address is already an active account elsewhere (a client, consultant or another bureau's auditor), so it cannot be moved into this bureau. Ask the bureau to invite a different address.";

// The shared dark sign-in palette (lib/signInPalette.ts), as Login, ResetPassword and SignedOut.

const API = import.meta.env.VITE_API_BASE_URL as string;

type InviteType = "consultancy" | "client" | "auditor" | "org_member" | "staff";

interface InviteInfo {
  type: InviteType;
  email: string;
  metadata: Record<string, string>;
  consultancy: { name: string; slug: string } | null;
  bureau: { name: string } | null;
  organization: { name: string } | null;
  /** What the sign-in policy of what this invite joins allows (backend/src/lib/signInPolicy.ts). */
  signIn?: { passwordAllowed: boolean; providers: Array<"entra_id" | "google_workspace" | "saml"> };
}

/** Refusals by the sign-in policy, from POST /invites/redeem and the Google invite callback. */
const POLICY_MESSAGES: Record<string, string> = {
  sso_required: "This invitation needs a Microsoft or Google work account; passwords are not accepted.",
  personal_account: "Use your work account. Personal Microsoft and Google accounts are not accepted here.",
  tenant_not_allowed: "That work account belongs to a Microsoft tenant or Google Workspace that has not been approved. Ask whoever invited you.",
  provider_not_allowed: "This way of signing in is not allowed here. Use one of the other options.",
};

function useToken() {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") ?? "";
}

// ── Sign-in choice (Microsoft, Google or a password; every invite type) ───
//
// The invited email is the identity; the provider only proves the redeemer
// controls it. Personal Gmail and personal Microsoft accounts are therefore
// accepted alongside Workspace / Entra tenants. Microsoft redeems via a popup
// and POST /invites/redeem; Google redirects through the API's OIDC callback
// and lands on /login already signed in. A password is bound to the invited
// address the same way — the link that got here was delivered to it — and the
// account then goes straight to authenticator-app setup.

function SsoForm({ token, info }: { token: string; info: InviteInfo }) {
  const { instance } = useMsal();
  const [loading,       setLoading]       = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [done,          setDone]          = useState(false);
  const [error,         setError]         = useState("");

  const [passwordMode,  setPasswordMode]  = useState(false);
  // The API enforces the policy at redemption; this only stops offering what it would refuse.
  const passwordAllowed  = info.signIn?.passwordAllowed ?? true;
  const microsoftAllowed = info.signIn?.providers.includes("entra_id") ?? true;
  const googleAllowed    = info.signIn?.providers.includes("google_workspace") ?? true;
  const [displayName,   setDisplayName]   = useState("");
  const [password,      setPassword]      = useState("");
  const [confirm,       setConfirm]       = useState("");
  const [loadingPw,     setLoadingPw]     = useState(false);

  // Detect a redirect back from the Google invite callback.
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const searchParams = new URLSearchParams(window.location.search);
    // The API set the session cookie on its redirect; the fragment only says so.
    const gaSession = hashParams.get("ga_session") === "1";
    const googleErr = searchParams.get("google_error");

    if (gaSession) {
      window.history.replaceState({}, "", window.location.pathname + window.location.search.replace(/&?google_error=[^&]*/g, ""));
      setDone(true);
      return;
    }
    if (googleErr) {
      window.history.replaceState({}, "", window.location.pathname);
      const MESSAGES: Record<string, string> = {
        invite_invalid: "The invite link is no longer valid. Please request a new one.",
        email_mismatch: "Your Google account email doesn't match the invite. Use the invited address.",
        auth_failed:    "Google sign-in failed. Please try again.",
        token_invalid:  "Could not verify your Google account. Please try again.",
        cancelled:      "Google sign-in was cancelled.",
        invalid_state:  "The sign-in link expired. Please try again.",
        account_conflict: ACCOUNT_CONFLICT_MESSAGE,
        ...POLICY_MESSAGES,
      };
      setError(MESSAGES[googleErr] ?? "Google sign-in failed. Please try again.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleMicrosoft() {
    setError(""); setLoading(true);
    try {
      const result = await instance.loginPopup({ ...loginRequest, prompt: "select_account" });
      const res = await fetch(`${API}/invites/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: info.type, token, entraAccessToken: result.accessToken }),
      });
      const body = await res.json();
      if (!res.ok) {
        // The API answers machine codes; the one a bureau invitee can hit is
        // spelled out, anything else falls back to the message as given.
        setError(body.error === "account_conflict" ? ACCOUNT_CONFLICT_MESSAGE : (POLICY_MESSAGES[body.error] ?? body.message ?? body.error ?? "Redemption failed."));
        return;
      }
      setDone(true);
    } catch (e: unknown) {
      if (e && typeof e === "object" && "errorCode" in e && (e as { errorCode: string }).errorCode === "user_cancelled") {
        setError("Sign-in was cancelled. Please try again.");
      } else {
        setError("Sign-in failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setError(""); setLoadingGoogle(true);
    try {
      // credentials: the API sets a nonce cookie that the SSO callback checks. The invite
      // token goes in the body, never the URL; the API keeps it out of the OAuth state too.
      const res = await fetch(`${API}/auth/google/invite-sso-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = await res.json() as { url?: string; error?: string };
      if (!res.ok || !body.url) {
        setError(body.error ?? "Could not start Google sign-in. Please try again.");
        setLoadingGoogle(false);
        return;
      }
      window.location.href = body.url;
    } catch {
      setError("Could not start Google sign-in. Please try again.");
      setLoadingGoogle(false);
    }
  }

  async function handlePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!passwordLooksAcceptable(password) || confirm !== password) return;
    setError(""); setLoadingPw(true);
    try {
      const res = await fetch(`${API}/invites/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: info.type, token, password, displayName: displayName.trim() || undefined }),
      });
      const body = await res.json().catch(() => ({})) as { error?: unknown; message?: string; accessToken?: string };
      if (!res.ok || !body.accessToken) {
        setError(body.message ?? (typeof body.error === "string" ? body.error : "Could not create the account. Please try again."));
        return;
      }
      // The session can only enrol the authenticator app; the app shows that next.
      startLocalSession(body.accessToken, false);
      window.location.href = "/";
    } catch {
      setError("Could not create the account. Please try again.");
    } finally {
      setLoadingPw(false);
    }
  }

  if (done) {
    if (info.type === "consultancy") {
      return (
        <SuccessBanner>
          <strong>{info.consultancy?.name ?? "Your firm"}</strong> is now linked to the platform.
          Your team can now <a href="/" style={{ color: C.success }}>sign in</a>.
        </SuccessBanner>
      );
    }
    return (
      <SuccessBanner>
        Your account is set up. You can now{" "}
        <a href="/" style={{ color: C.success }}>sign in</a>.
      </SuccessBanner>
    );
  }

  function subtitle() {
    if (info.type === "consultancy") {
      return <>Sign in with your firm's account to link it to <strong>{info.consultancy?.name ?? "your firm"}</strong>.</>;
    }
    if (info.type === "staff") {
      return <>Sign in with your work account to join <strong>{info.consultancy?.name ?? "your consultancy"}</strong>.</>;
    }
    if (info.type === "auditor") {
      return <>Sign in to link your identity and activate your auditor account for <strong>{info.bureau?.name ?? "the bureau"}</strong>.</>;
    }
    return <>Sign in to set up your access to <strong>{info.organization?.name ?? "the platform"}</strong>.</>;
  }

  const busy = loading || loadingGoogle || loadingPw;

  if (passwordMode) {
    const mismatch = confirm.length > 0 && confirm !== password;
    const canSubmit = passwordLooksAcceptable(password) && confirm === password && !busy;
    return (
      <form onSubmit={handlePassword} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 12, color: C.subtle, lineHeight: 1.6 }}>
          Your account will be <strong>{info.email}</strong>. You will sign in with this password and a code from an authenticator app on your phone,
          which you set up in the next step.
        </div>
        <div>
          <label style={labelStyle} htmlFor="invite-name">Your name</label>
          <input id="invite-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={100} autoComplete="name" style={fieldInput} placeholder="As shown to your team" />
        </div>
        <div>
          <label style={labelStyle} htmlFor="invite-password">Choose a password</label>
          <input id="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" style={fieldInput} />
          <div style={{ marginTop: 6 }}><PasswordRules password={password} dark /></div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="invite-confirm">Confirm password</label>
          <input id="invite-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" style={fieldInput} />
          {mismatch && <div style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>The passwords do not match.</div>}
        </div>
        {error && <div style={{ fontSize: 12, color: C.danger }}>{error}</div>}
        <button type="submit" disabled={!canSubmit} style={btnStyle(!canSubmit)}>
          {loadingPw ? "Creating account…" : "Create account and set up authenticator"}
        </button>
        <button type="button" onClick={() => { setPasswordMode(false); setError(""); }} disabled={busy} style={{ background: "none", border: "none", color: C.subtle, fontSize: 12, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>
          Use Microsoft or Google instead
        </button>
      </form>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 12, color: C.subtle }}>{subtitle()}</div>
      <div style={{ fontSize: 11, color: C.subtle }}>
        {passwordAllowed
          ? <>Use the account for <strong>{info.email}</strong>: a Microsoft or Google account, or create a password.</>
          : <>Sign in with the work account for <strong>{info.email}</strong>. Single sign-on is required here.</>}
      </div>
      {error && <div style={{ fontSize: 12, color: C.danger }}>{error}</div>}
      {microsoftAllowed && (
        <button onClick={handleMicrosoft} disabled={busy} style={btnStyle(busy)}>
          {loading ? "Signing in…" : "Sign in with Microsoft"}
        </button>
      )}
      {microsoftAllowed && googleAllowed && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ fontSize: 11, color: C.subtle }}>or</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>
      )}
      {googleAllowed && <button
        onClick={handleGoogle}
        disabled={busy}
        style={{ ...signInSecondaryButton, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        {loadingGoogle ? "Redirecting…" : "Sign in with Google"}
      </button>}
      {passwordAllowed && (
        <>
          <button
            onClick={() => { setPasswordMode(true); setError(""); }}
            disabled={busy}
            style={{ ...signInSecondaryButton, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1 }}
          >
            Create a password instead
          </button>
          <div style={{ fontSize: 11, color: C.subtle, lineHeight: 1.5 }}>
            No Microsoft or Google account for work? A password works too; you will also set up an authenticator app on your phone.
          </div>
        </>
      )}
    </div>
  );
}

// ── Small shared helpers ──────────────────────────────────────────────────

function SuccessBanner({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: C.successBg, border: `1px solid ${C.success}`, borderRadius: 3, padding: "16px 20px", fontSize: 13, color: C.success, lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = { fontSize: 11, color: C.subtle, fontWeight: 600, display: "block", marginBottom: 4 };
const fieldInput: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 3,
  border: `1px solid ${C.border}`, background: C.inputBg, color: C.heading, fontSize: 13, outline: "none",
};

function btnStyle(disabled: boolean | undefined): React.CSSProperties {
  return {
    padding: "10px 18px", borderRadius: 3, border: "none",
    background: C.accent, color: C.btnText, fontWeight: 600, fontSize: 13,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
  };
}

function inviteTypeLabel(type: InviteType) {
  if (type === "consultancy") return "Consultancy";
  if (type === "auditor") return "Auditor";
  if (type === "org_member") return "Team Member";
  if (type === "staff") return "Consultant";
  return "Client";
}

function inviteContextLine(info: InviteInfo): string | null {
  if (info.type === "consultancy" && info.consultancy) return `Firm: ${info.consultancy.name}`;
  if (info.type === "client" && info.organization) return `Organisation: ${info.organization.name}`;
  if (info.type === "org_member" && info.organization) return `Organisation: ${info.organization.name}`;
  if (info.type === "auditor" && info.bureau) return `Bureau: ${info.bureau.name}`;
  return null;
}

// ── Main page ─────────────────────────────────────────────────────────────

export function InviteRedeemPage() {
  const token = useToken();
  const [info,      setInfo]      = useState<InviteInfo | null>(null);
  const [fetchDone, setFetchDone] = useState(false);
  const [invalid,   setInvalid]   = useState(false);

  useEffect(() => {
    if (!token) { setInvalid(true); setFetchDone(true); return; }
    fetch(`${API}/invites/redeem?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) { setInvalid(true); return; }
        setInfo(await res.json());
      })
      .catch(() => setInvalid(true))
      .finally(() => setFetchDone(true));
  }, [token]);

  const contextLine = info ? inviteContextLine(info) : null;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.font,
        padding: 16,
        position: "relative",
      }}
    >
      <BlueprintBackdrop surface="login" />

      <div
        style={{ ...signInSheet, maxWidth: 440 }}
      >
        {!fetchDone && (
          <div style={{ color: C.subtle, fontSize: 13, textAlign: "center", padding: "24px 0" }}>
            Validating invite…
          </div>
        )}

        {fetchDone && invalid && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.heading, marginBottom: 8 }}>
              Invite link invalid or expired
            </div>
            <div style={{ fontSize: 13, color: C.subtle, lineHeight: 1.6 }}>
              This invite link is no longer valid. It may have already been used or expired after 72 hours.
              Please contact your administrator to request a new invite.
            </div>
          </div>
        )}

        {fetchDone && info && (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: C.subtle, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                {inviteTypeLabel(info.type)} invite
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.heading }}>
                You've been invited
              </div>
              {contextLine && (
                <div style={{ fontSize: 12, color: C.subtle, marginTop: 4 }}>{contextLine}</div>
              )}
            </div>

            <SsoForm token={token} info={info} />
          </>
        )}
      </div>
    </div>
  );
}
